import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Loader2, AlertCircle, CheckCircle, Sun, Moon, FileCheck, ShieldCheck, KeyRound, ArrowLeft, Smartphone, Lock } from 'lucide-react';
import { useAuth } from '../stores/authStore';
import { useTheme } from '../stores/themeStore';
import { authApi } from '../services/api';

const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { login, verifyMagicLink, verify2fa, initialized } = useAuth();
  const [step, setStep] = useState<'email' | 'verify' | '2fa'>('email');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [devLink, setDevLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tempToken2fa, setTempToken2fa] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [isBackupMode, setIsBackupMode] = useState(false);
  const autoVerifyStarted = useRef(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === '2fa') {
      const timer = setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [step, isBackupMode]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    setLoading(true);
    setLoginEmail(data.email);
    try {
      const result = await login(data.email);

      // If user has 2FA enabled, immediately switch to 2FA input
      if (result && (result as any).requires_2fa) {
        setTempToken2fa((result as any).temp_token);
        if ((result as any).email) setLoginEmail((result as any).email);
        setStep('2fa');
        setError(null);
        setSuccess(null);
        return;
      }

      let token = (result as any)?.token;
      if (!token && result?.devLink) {
        try {
          const url = new URL(result.devLink);
          token = url.searchParams.get('token');
        } catch {
          // ignore
        }
      }

      if (token) {
        setSuccess('Đang xác thực thông tin tài khoản...');
        const res = await verifyMagicLink(token, 'login');
        if (res && (res as any).requires_2fa) {
          setTempToken2fa((res as any).temp_token);
          setStep('2fa');
          setError(null);
          setSuccess(null);
          return;
        }
        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
        navigate('/dashboard', { replace: true });
        return;
      }

      setMagicLinkSent(true);
      setStep('verify');
      setSuccess('Đã gửi liên kết đăng nhập đến email của bạn. Vui lòng kiểm tra hộp thư.');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Có lỗi xảy ra. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const verifySchema = z.object({
    token: z.string().min(1, 'Vui lòng nhập token'),
  });

  type VerifyForm = z.infer<typeof verifySchema>;

  const {
    register: registerVerify,
    handleSubmit: handleSubmitVerify,
    formState: { errors: verifyErrors },
  } = useForm<VerifyForm>({
    resolver: zodResolver(verifySchema),
  });

  const onVerifySubmit = async (data: VerifyForm) => {
    setError(null);
    setLoading(true);
    try {
      if (!data.token) {
        throw new Error('Vui lòng nhập mã xác thực');
      }
      const res = await verifyMagicLink(data.token, 'login');
      if (res && (res as any).requires_2fa) {
        setTempToken2fa((res as any).temp_token);
        setStep('2fa');
        setSuccess(null);
        return;
      }
      setSuccess('Đăng nhập thành công!');
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Liên kết không hợp lệ hoặc đã hết hạn');
    } finally {
      setLoading(false);
    }
  };

  const handle2faSubmit = async (e?: React.FormEvent, customCode?: string) => {
    if (e) e.preventDefault();
    const codeToUse = (customCode !== undefined ? customCode : totpCode).trim();
    if (!tempToken2fa || !codeToUse) return;
    setError(null);
    setLoading(true);
    try {
      await verify2fa(tempToken2fa, codeToUse);
      setSuccess('Xác thực 2 bước thành công! Đang chuyển hướng...');
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Mã xác thực 2FA không chính xác hoặc đã hết hạn');
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify when the user arrives from the magic-link email
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token || !initialized || autoVerifyStarted.current) return;

    autoVerifyStarted.current = true;
    // Strip token from browser address bar immediately so re-renders or page refreshes don't re-execute
    window.history.replaceState({}, document.title, window.location.pathname);

    setStep('verify');
    setLoading(true);
    verifyMagicLink(token, params.get('purpose') || 'login')
      .then((res) => {
        if (res && (res as any).requires_2fa) {
          setTempToken2fa((res as any).temp_token);
          setStep('2fa');
          setSuccess(null);
          return;
        }
        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
        navigate('/dashboard', { replace: true });
      })
      .catch((err: any) => {
        setError(err.response?.data?.error?.message || 'Liên kết không hợp lệ hoặc đã hết hạn');
      })
      .finally(() => setLoading(false));
  }, [location.search, initialized, navigate, verifyMagicLink]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-[#0b0f19] text-slate-900 dark:text-slate-100 px-4 transition-colors relative">
      {/* Top right theme toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800 border border-slate-200 dark:border-white/10 transition-all"
          title={theme === 'dark' ? 'Chuyển sang giao diện Sáng' : 'Chuyển sang giao diện Tối'}
        >
          {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
        </button>
      </div>

      <div className="w-full max-w-md py-8">
        {/* Logo */}
        <div className="text-center mb-8">
          {step === '2fa' ? (
            <div className="relative inline-flex items-center justify-center mb-4">
              <div className="absolute inset-0 rounded-2xl bg-sky-500/25 blur-xl"></div>
              <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-tr from-sky-500 to-indigo-600 flex items-center justify-center text-white shadow-xl shadow-sky-500/30">
                <ShieldCheck className="w-7 h-7" />
              </div>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2.5 mb-3">
              <div className="w-11 h-11 bg-gradient-to-br from-sky-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20 text-white">
                <FileCheck className="w-6 h-6" />
              </div>
              <span className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">PDF Sign</span>
            </div>
          )}

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {step === 'email' ? 'Đăng nhập hệ thống' : step === '2fa' ? 'Bảo mật hai lớp (2FA)' : 'Xác thực tài khoản'}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
            {step === 'email' ? (
              'Nền tảng ký điện tử & phân luồng văn bản đám mây'
            ) : step === '2fa' ? (
              <span className="inline-flex items-center gap-1.5">
                Xác thực phiên đăng nhập cho <span className="font-semibold text-slate-800 dark:text-slate-200">{loginEmail || 'tài khoản'}</span>
              </span>
            ) : (
              'Nhập mã xác thực hoặc nhấn liên kết đăng nhập'
            )}
          </p>
        </div>

        {/* Card */}
        <div className="card p-6 sm:p-8 border border-slate-200/80 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-none bg-white dark:bg-[#111827]">
          {error && (
            <div className="mb-6 p-3.5 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-500/20 rounded-xl flex items-center gap-3 text-rose-700 dark:text-rose-300 text-xs">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-700 dark:text-emerald-300 text-xs">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <p>{success}</p>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`input pl-10 ${errors.email ? 'input-error' : ''}`}
                    placeholder="nguyen.van.a@company.com"
                    disabled={loading}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang đăng nhập...
                  </>
                ) : (
                  'Đăng nhập'
                )}
              </button>
            </form>
          ) : step === '2fa' ? (
            <form onSubmit={handle2faSubmit} className="space-y-6">
              {/* Notification Banner */}
              <div className="p-3.5 bg-gradient-to-r from-sky-500/10 via-indigo-500/5 to-sky-500/10 border border-sky-500/20 rounded-2xl flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-sky-500/20 flex items-center justify-center flex-shrink-0 text-sky-500 shadow-inner">
                  <Smartphone className="w-4 h-4" />
                </div>
                <div className="text-xs space-y-0.5">
                  <p className="font-medium text-slate-800 dark:text-slate-200">
                    Ứng dụng <strong>Google Authenticator</strong>
                  </p>
                  <p className="text-slate-500 dark:text-slate-400">
                    Mở ứng dụng trên điện thoại để lấy mã 6 chữ số
                  </p>
                </div>
              </div>

              {!isBackupMode ? (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Mã xác thực 6 chữ số
                    </label>
                    <span className="text-[11px] text-sky-500 font-mono flex items-center gap-1">
                      <Lock className="w-3 h-3" /> TOTP Live
                    </span>
                  </div>

                  {/* Segmented OTP 6-Digit Slots */}
                  <div
                    className="relative flex justify-center items-center gap-2 sm:gap-2.5 my-3 cursor-text select-none"
                    onClick={() => otpInputRef.current?.focus()}
                  >
                    <input
                      ref={otpInputRef}
                      id="totpHiddenInput"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setTotpCode(val);
                        if (val.length === 6) {
                          handle2faSubmit(undefined, val);
                        }
                      }}
                      className="absolute inset-0 opacity-0 cursor-text w-full h-full z-20 pointer-events-auto"
                      autoFocus
                      disabled={loading}
                    />

                    {/* First 3 boxes */}
                    <div className="flex gap-2">
                      {[0, 1, 2].map((idx) => {
                        const char = totpCode[idx];
                        const isCurrent = totpCode.length === idx;
                        return (
                          <div
                            key={idx}
                            className={`w-11 h-14 sm:w-12 sm:h-15 rounded-xl border flex items-center justify-center font-mono text-2xl font-bold transition-all duration-150 ${
                              isCurrent
                                ? 'border-sky-500 bg-sky-500/10 dark:bg-sky-500/20 ring-4 ring-sky-500/20 shadow-md shadow-sky-500/10 text-sky-600 dark:text-sky-300'
                                : char
                                ? 'border-slate-300 dark:border-white/20 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-white shadow-sm'
                                : 'border-slate-200 dark:border-white/10 bg-slate-100/60 dark:bg-slate-900/40 text-slate-300 dark:text-slate-600'
                            }`}
                          >
                            {char || (isCurrent ? (
                              <span className="w-0.5 h-6 bg-sky-500 animate-pulse rounded-full" />
                            ) : (
                              '•'
                            ))}
                          </div>
                        );
                      })}
                    </div>

                    {/* Middle separator */}
                    <div className="w-1.5 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-0.5" />

                    {/* Last 3 boxes */}
                    <div className="flex gap-2">
                      {[3, 4, 5].map((idx) => {
                        const char = totpCode[idx];
                        const isCurrent = totpCode.length === idx;
                        return (
                          <div
                            key={idx}
                            className={`w-11 h-14 sm:w-12 sm:h-15 rounded-xl border flex items-center justify-center font-mono text-2xl font-bold transition-all duration-150 ${
                              isCurrent
                                ? 'border-sky-500 bg-sky-500/10 dark:bg-sky-500/20 ring-4 ring-sky-500/20 shadow-md shadow-sky-500/10 text-sky-600 dark:text-sky-300'
                                : char
                                ? 'border-slate-300 dark:border-white/20 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-white shadow-sm'
                                : 'border-slate-200 dark:border-white/10 bg-slate-100/60 dark:bg-slate-900/40 text-slate-300 dark:text-slate-600'
                            }`}
                          >
                            {char || (isCurrent ? (
                              <span className="w-0.5 h-6 bg-sky-500 animate-pulse rounded-full" />
                            ) : (
                              '•'
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 mt-2 text-center">
                    Tự động đăng nhập khi nhập đủ 6 chữ số
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <label htmlFor="backupCode" className="label flex items-center justify-between">
                    <span>Mã dự phòng (8 ký tự)</span>
                    <KeyRound className="w-4 h-4 text-slate-400" />
                  </label>
                  <input
                    id="backupCode"
                    type="text"
                    autoFocus
                    maxLength={10}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                    className="input tracking-widest text-center text-lg font-mono uppercase"
                    placeholder="ABCD-1234"
                    disabled={loading}
                  />
                  <p className="text-[11px] text-slate-400 text-center">
                    Nhập một trong các mã dự phòng bạn đã lưu khi cài đặt 2FA
                  </p>
                </div>
              )}

              {/* Mode switch */}
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setIsBackupMode(!isBackupMode);
                    setTotpCode('');
                    setError(null);
                  }}
                  className="text-xs font-medium text-sky-600 dark:text-sky-400 hover:text-sky-500 hover:underline inline-flex items-center gap-1.5 transition-colors"
                >
                  {isBackupMode ? (
                    <>
                      <Smartphone className="w-3.5 h-3.5" />
                      Nhập mã 6 số từ Google Authenticator
                    </>
                  ) : (
                    <>
                      <KeyRound className="w-3.5 h-3.5" />
                      Không mang điện thoại? Dùng mã dự phòng
                    </>
                  )}
                </button>
              </div>

              {/* Action Buttons */}
              <button
                type="submit"
                className="w-full py-3.5 px-4 rounded-xl font-medium text-sm text-white bg-gradient-to-r from-sky-500 via-sky-600 to-indigo-600 hover:from-sky-400 hover:via-sky-500 hover:to-indigo-500 shadow-lg shadow-sky-500/25 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                disabled={loading || !totpCode.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Đang xác thực bảo mật...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Xác nhận & Đăng nhập
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setTotpCode('');
                  setTempToken2fa(null);
                  setIsBackupMode(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="w-full py-2.5 text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60 rounded-xl transition-all flex items-center justify-center gap-1.5"
                disabled={loading}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Quay lại màn hình đăng nhập
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmitVerify(onVerifySubmit)} className="space-y-6">
              {magicLinkSent && (
                <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <p className="text-sm text-primary-800">
                    Đã gửi liên kết đến email của bạn. Vui lòng kiểm tra hộp thư (kể cả thư rác/spam).
                  </p>
                  <p className="text-xs text-primary-600 mt-1">
                    Liên kết sẽ hết hạn sau 15 phút.
                  </p>
                </div>
              )}

              {import.meta.env.DEV && devLink && (
                <button
                  type="button"
                  onClick={async () => {
                    const url = new URL(devLink);
                    const token = url.searchParams.get('token');
                    if (token) {
                      setLoading(true);
                      try {
                        setSuccess('Đang xác thực...');
                        const res = await verifyMagicLink(token, 'login');
                        if (res && (res as any).requires_2fa) {
                          setTempToken2fa((res as any).temp_token);
                          setStep('2fa');
                          setSuccess(null);
                          return;
                        }
                        setSuccess('Đăng nhập thành công! Đang chuyển hướng...');
                        setTimeout(() => navigate('/dashboard'), 600);
                      } catch (err: any) {
                        setError(err.response?.data?.error?.message || 'Lỗi xác thực token');
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="w-full text-left p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800 hover:bg-warning-100 transition-colors"
                >
                  <strong>[DEV]</strong> Nhấn vào đây để xác thực và đăng nhập ngay.
                </button>
              )}

              <div>
                <label htmlFor="token" className="label">
                  Mã xác thực (tùy chọn - tự động điền nếu click từ email)
                </label>
                <input
                  {...registerVerify('token')}
                  id="token"
                  type="text"
                  className={`input ${verifyErrors.token ? 'input-error' : ''}`}
                  placeholder="Nhập token từ email..."
                  disabled={loading}
                />
                {verifyErrors.token && (
                  <p className="mt-1 text-sm text-danger-600">{verifyErrors.token.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Đang xác thực...
                  </>
                ) : (
                  'Xác thực và đăng nhập'
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setMagicLinkSent(false);
                  setError(null);
                  setSuccess(null);
                }}
                className="btn-ghost w-full"
                disabled={loading}
              >
                Quay lại nhập email
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Bằng cách đăng nhập, bạn đồng ý với</p>
            <div className="flex justify-center gap-4 mt-1">
              <a href="#" className="text-primary-600 hover:text-primary-700">Điều khoản sử dụng</a>
              <a href="#" className="text-primary-600 hover:text-primary-700">Chính sách bảo mật</a>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Cần hỗ trợ? <a href="#" className="text-primary-600 hover:text-primary-700">Liên hệ chúng tôi</a></p>
        </div>
      </div>
    </div>
  );
}